/**
 * Single source of truth for the Words Cloud `config` bundle.
 *
 * Every key the component tree reads from `config` is listed here with its
 * default value. Historically these defaults were scattered across Chart.jsx
 * destructure literals, useWordProcessor.js destructure literals, and ad-hoc
 * `state.x || fallback` expressions in Controls.jsx; some keys (`junk`,
 * `stop1..stop5`, `customMaskId`, `currentPresetId`) had no component-side
 * default at all and relied on the stats-page shell to supply them.
 *
 * `rawData` is intentionally excluded — it is the corpus payload and has no
 * sensible default; callers MUST supply it.
 *
 * `useRawData` is intentionally excluded — it is a `model` property, not a
 * `config` property (Chart.jsx reads it as `model.useRawData`).
 */
// The cloud's ground. It stands off the card it sits on rather than running to the card's edge, and
// takes that card's own corner rather than a squarer one. Both are read by the markup rather than by
// a stylesheet: the export path serializes the <svg> and carries no styles with it, so a radius set
// in CSS would be missing from every exported image.
export const CLOUD_INSET = 10
export const CLOUD_RADIUS = 14

// What a colour theme paints its ground with unless it names its own. Near-black rather than black:
// flat #000 sits outside the page's palette and reads as a hole punched in it.
export const THEME_BACKGROUND = '#0B172A'

export const DEFAULT_CONFIG = Object.freeze({
  currentPresetId: 'c',

  // Visual — layout
  angles: Object.freeze([0, 0, 90]),
  orientation: 'h',
  fontFamily: 'SaunaB',
  scaleFunction: 'sqrt',
  rangeStart: 35,
  rangeEnd: 95,
  maxWords: 300,
  randIndex: 0,

  // Visual — color
  colorStyle: 'adj_levels',
  colors: Object.freeze(['#8CC63F', '#60AC66', '#419982', '#1D83A2', '#1868A6', '#135182', '#003863']),
  background: THEME_BACKGROUND,

  // Mask
  mask: '',
  customMaskId: '-1',
  customMaskPath: '',

  // Text-processing filters
  spell: 1,
  caps: 1,
  mergeWords: 1,
  twoWords: 0,
  junk: 1,
  stop1: 1,
  stop2: 1,
  stop3: 0,
  stop4: 0,
  stop5: 0,
  blackList: Object.freeze([]),

  // Interaction state
  activeWord: ''
})

/**
 * Merge caller-supplied config over DEFAULT_CONFIG.
 *
 * `undefined` values in `config` are treated as "not supplied" and fall back
 * to the default — matching the behavior of destructure defaults
 * (`const { x = 1 } = { x: undefined }` yields `x === 1`).
 *
 * Explicit `null` is left in place — that represents a deliberate
 * caller-intended null, not a missing key.
 *
 * This is a shallow merge: array/object values in `config` replace the
 * defaults wholesale rather than being deep-merged.
 *
 * @param {object} [config] - Caller-supplied config (may be partial or missing)
 * @returns {object} - A new config object with every DEFAULT_CONFIG key populated
 */
export function withDefaults(config) {
  const result = { ...DEFAULT_CONFIG }
  if (!config) return result
  for (const key of Object.keys(config)) {
    if (config[key] !== undefined) result[key] = config[key]
  }
  return result
}
